"use client"

import Link from "next/link"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { useState } from "react"
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui"

interface NavigationProps {
  backUrl?: string | null
  backLabel?: string
}

export function Navigation({ backUrl, backLabel }: NavigationProps) {
  const pathname = usePathname()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const isDashboard = pathname === '/dashboard'

  return (
    <header className="sticky top-4 mx-4 lg:mx-6 flex items-center justify-between px-4 lg:px-6 py-4 rounded-2xl backdrop-blur-xl bg-[#0A0D14]/70 border border-white/10 shadow-2xl z-40 font-sans antialiased">
      <div className="flex items-center gap-3">
        <Link href="/dashboard" className="flex items-center gap-4 group">
          <Image
            src="/IRMA_Token.png"
            alt="IRMA Token Logo"
            width={96}
            height={96}
            className="group-hover:scale-105 transition-transform"
          />
        </Link>
      </div>

      {/* Desktop Navigation */}
      <nav className="hidden lg:flex items-center gap-5 text-sm font-semibold absolute left-1/2 -translate-x-1/2">
        <Link
          href="/dashboard"
          className={`transition ${isDashboard ? 'text-white' : 'text-[#8B949E] hover:text-white'}`}
        >
          Dashboard
        </Link>
        <Link
          href="/oracle-staking-test"
          className={`transition ${pathname === '/oracle-staking-test' ? 'text-white' : 'text-[#8B949E] hover:text-white'}`}
        >
          Oracle Test
        </Link>
      </nav>

      {/* Desktop Buttons */}
      <div className="hidden lg:flex items-center gap-3">
        {backUrl && (
          <a
            href={backUrl}
            className="flex items-center justify-center h-10 rounded-lg border border-[#25e4e8]/30 bg-[#25e4e8]/10 px-4 text-xs font-semibold text-[#25e4e8] transition hover:border-[#25e4e8] hover:bg-[#25e4e8]/20 whitespace-nowrap"
          >
            {backLabel || 'Back'}
          </a>
        )}
        <WalletMultiButton />
      </div>

      {/* Mobile Menu Toggle */}
      <div className="flex lg:hidden items-center gap-3">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-white p-2 hover:bg-white/5 rounded-lg transition"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Dropdown Menu */}
      {mobileMenuOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 backdrop-blur-xl bg-[#13161C]/80 border border-white/10 rounded-2xl lg:hidden shadow-2xl mx-4">
          <nav className="flex flex-col p-4 space-y-3">
            <Link
              href="/dashboard"
              onClick={() => setMobileMenuOpen(false)}
              className={`px-4 py-3 rounded-lg transition ${
                isDashboard ? 'text-white bg-white/5' : 'text-[#8B949E] hover:text-white hover:bg-white/5'
              }`}
            >
              Dashboard
            </Link>
            <Link
              href="/oracle-staking-test"
              onClick={() => setMobileMenuOpen(false)}
              className={`px-4 py-3 rounded-lg transition ${
                pathname === '/oracle-staking-test' ? 'text-white bg-white/5' : 'text-[#8B949E] hover:text-white hover:bg-white/5'
              }`}
            >
              Oracle Test
            </Link>
            <div className="pt-3 border-t border-white/5 space-y-3">
              {backUrl && (
                <a
                  href={backUrl}
                  className="flex items-center justify-center h-10 rounded-lg border border-[#25e4e8]/30 bg-[#25e4e8]/10 px-4 text-xs font-semibold text-[#25e4e8] transition hover:border-[#25e4e8] hover:bg-[#25e4e8]/20"
                >
                  {backLabel || 'Back'}
                </a>
              )}
              <div className="w-full">
                <WalletMultiButton />
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
